import {
  Box,
  Button,
  Flex,
  Link,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
  useReactTable,
  VisibilityTableState,
  ColumnOrderTableState,
  GroupingTableState,
  ColumnSizingTableState,
  ColumnPinningTableState,
  SortingTableState,
  ExpandedTableState,
  PaginationTableState,
  RowSelectionTableState,
} from '@tanstack/react-table';
import React, { useState, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';

import { ResolvedAggregateLineItemEntryGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { Typography } from 'packages/common-frontend/components/Typography';
import { usePermissionGuard } from '~/core/PermissionGuard';
import { permissions as loansPermissions } from '~/core/routes/loans/permissions';
import { colors } from '~/packages/common-frontend/colors';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';
import { SearchBarInput } from '~/packages/common-frontend/components/SearchAndCopyComponents';
import { TanStackPaginationControl } from '~/packages/common-frontend/components/TanStack';
import { tableHeaderStyle } from '~/packages/common-frontend/styles';
import {
  formatCurrencyAmount,
  getTAccountTypeLabel,
  truncateUuid,
} from '~/packages/common-frontend/utils';
import { TransactionsFilterState } from '~/packages/transactions/hooks/transactionsHooks';
import { useGetDailyResolvedAggregateLineItemEntriesQuery } from '~/packages/transactions/requests/transactions.gql.generated';

// types
type TanstackTableState = Partial<
  VisibilityTableState &
    ColumnOrderTableState &
    ColumnPinningTableState &
    SortingTableState &
    ExpandedTableState &
    GroupingTableState &
    ColumnSizingTableState &
    PaginationTableState &
    RowSelectionTableState
>;

const TABLE_COLUMN_CONFIGS: ColumnDef<
  ResolvedAggregateLineItemEntryGQLType,
  any
>[] = [
  {
    id: 'postingDate',
    accessorKey: 'postingDate',
    header: 'Posted',
    cell: function formatDate({ getValue }) {
      return commonDate.fullDateStrToHumanDateString(getValue());
    },
  },
  {
    id: 'tAccount',
    header: 'Account',
    accessorFn: (row) => getTAccountTypeLabel(row.tAccount),
    meta: {
      maxWidth: '15rem',
    },
  },
  {
    id: 'aggregateAmount',
    accessorKey: 'aggregateAmount',
    header: 'Amount',
    cell: function formatAmount({ getValue }) {
      return (
        <Text
          color={getValue() < 0 ? colors.green[400] : 'undefined'}
          fontWeight={getValue() < 0 ? 500 : 'normal'}
        >
          {formatCurrencyAmount(getValue(), Bank.Currency.USD)}
        </Text>
      );
    },
  },
  {
    id: 'creditFacilityName',
    accessorKey: 'creditFacilityName',
    header: 'Facility',
    meta: {
      maxWidth: '15rem',
    },
  },
  {
    id: 'organizationName',
    accessorKey: 'organizationName',
    header: 'Counterparty',
  },
  {
    id: 'loanName',
    accessorKey: 'loanName',
    header: 'Loan Name',
    cell: function formatLoanName({ getValue }) {
      return getValue() || '-';
    },
  },
  {
    id: 'loanId',
    accessorKey: 'loanId',
    header: 'Loan ID',
    cell: function formatLoanId({ getValue }) {
      return getValue() ? (
        <Link as={RouterLink} to={`/loans/${getValue()}`}>
          <Typography
            colorScheme="blue"
            variant="base-muted"
            fontWeight="medium"
          >
            {truncateUuid(getValue())}
          </Typography>
        </Link>
      ) : (
        '-'
      );
    },
  },
  // TODO: @markhaghani add external facility id
  {
    id: 'externalLoanId',
    accessorKey: 'externalLoanId',
    header: 'External Loan ID',
    cell: function formatExternalLoanId({ getValue }) {
      return getValue() ?? '-';
    },
  },
  // TODO: @markhaghani let's add this in once we have this available
  // {
  //   id: 'invoiceId',
  //   accessorKey: 'invoiceId',
  //   header: 'Invoice ID',
  //   cell: function formatInvoiceId({ getValue }) {
  //     return getValue() ? (
  //       <RouterLink to={`/invoices/${getValue()}`}>
  //         <Button variant="link" colorScheme="blue">
  //           {truncateUuid(getValue())}
  //         </Button>
  //       </RouterLink>
  //     ) : (
  //       '-'
  //     );
  //   },
  // },
];

export const TransactionsDailyLineItemsTable = ({
  creditFacilityIds,
  loanIds,
  accountTypes,
  startDate,
  endDate,
  showBackToTopButton = true,
  initialTanstackTableState = {
    pagination: {
      pageSize: 100,
      pageIndex: 0,
    },
  },
  hasActiveFilters,
}: TransactionsFilterState & {
  showBackToTopButton?: boolean;
  initialTanstackTableState?: TanstackTableState;
  hasActiveFilters?: boolean;
}) => {
  const isLoansViewEnabled = usePermissionGuard(loansPermissions);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    error,
    data: dailyLineItemsEntriesResult,
    loading,
  } = useGetDailyResolvedAggregateLineItemEntriesQuery({
    skip: accountTypes.length === 0,
    fetchPolicy: 'no-cache',
    variables: {
      creditFacilityIds,
      loanIds,
      accountTypes,
      startDate,
      endDate,
    },
  });

  // TODO: @markhaghani optimize search by adding fuzzy search from fuse
  const filteredEntries = useMemo(() => {
    if (!dailyLineItemsEntriesResult) {
      return [];
    }

    const { entries } = dailyLineItemsEntriesResult;
    const search = searchQuery.toLowerCase();
    if (!search) {
      return entries;
    }

    const isMatch = (input?: string | null): boolean => {
      if (!input) return false;
      return input.toLowerCase().includes(search);
    };

    // No need to support every attribute on the loan, so can just
    // single them out as needed and ignore the others.
    // Can add to this list as we deem it'll be helpful for now
    return entries.filter((entry) => {
      if (isMatch(getTAccountTypeLabel(entry.tAccount))) return true;
      if (isMatch(entry.creditFacilityName)) return true;
      if (isMatch(entry.organizationName)) return true;
      if (isMatch(entry.loanId)) return true;
      if (isMatch(entry.loanName)) return true;
      // TODO: @markhaghani let's add this in once we have this available
      // if (isMatch(entry.invoiceId)) return true;

      return false;
    });
  }, [searchQuery, dailyLineItemsEntriesResult]);

  const table = useReactTable({
    data: filteredEntries,
    columns: TABLE_COLUMN_CONFIGS.filter((column) => {
      if (isLoansViewEnabled === 'allow') return true;
      return column.id !== 'loanId';
    }),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      ...initialTanstackTableState,
    },
  });
  const tableRef = useRef<HTMLDivElement>(null);

  const csvDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });
  if (error) throw error;
  if (loading) return <Skeleton height="500px" w="100%" />;
  if (
    accountTypes.length === 0 ||
    dailyLineItemsEntriesResult?.entries.length === 0
  ) {
    return (
      <Flex py="2rem">
        <Typography>
          {hasActiveFilters
            ? 'No transactions were found for the selected filters. Adjust your selection.'
            : 'No transactions were found.'}
        </Typography>
      </Flex>
    );
  }

  return (
    <>
      <Flex
        sx={{ justifyContent: 'center', gap: '1rem', py: '1.5rem' }}
        ref={tableRef}
      >
        <SearchBarInput query={searchQuery} onChangeQuery={setSearchQuery} />
        <CsvXlsxDownloadSelect
          dataRows={csvDownload.dataRows}
          headers={csvDownload.headers}
          filenamePrefix="transaction-line-items"
        />
      </Flex>
      <TableContainer>
        <Table sx={{ borderColor: colors.gray[100], borderWidth: '1px' }}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th key={header.id} sx={tableHeaderStyle}>
                    {flexRender(
                      String(header.column.columnDef.header),
                      header.getContext(),
                    )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    sx={{
                      backgroundColor: 'white',
                      px: '1rem',
                      color: colors.black[70],
                      minWidth: '12rem',
                      maxWidth: cell.column.columnDef.meta?.maxWidth || 'auto',
                      whiteSpace: 'normal',
                    }}
                    // TODO: @haghanim add overflowX
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      {filteredEntries.length === 0 && (
        <Box
          backgroundColor={colors.gray[20]}
          minH="4rem"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text textAlign="center">No entries found.</Text>
        </Box>
      )}
      <TanStackPaginationControl table={table} />
      {showBackToTopButton && (
        <Flex justifyContent="right" mt="1rem">
          {filteredEntries.length > 0 && (
            <Button
              variant="link"
              onClick={() =>
                tableRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'end',
                })
              }
            >
              Back to the top
            </Button>
          )}
        </Flex>
      )}
    </>
  );
};
